import { Box, ButtonBS, Center, Flex, Text, VStack } from "@biblioteksentralen/react";
import { isCampaignSeason, isNonNil, ResolvedSommerles } from "@libry-content/common";
import { ReactNode } from "react";
import { ExternalLink } from "react-feather";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import { EventsList } from "../events/EventsList";
import { CampaignViewSection } from "../campaigns/CampaignViewSection";
import { useCampaignEvents } from "../campaigns/useCampaignEvents";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImagePreservingAspectRatio } from "../images/SanityImagePreservingAspectRatio";
import { useCommonData } from "../layout/CommonDataProvider";
import { ListsAndRecommendationsList } from "../lists/ListsAndRecommendationsList";
import { sortRecommendationBookListByDate } from "../recommendation/utils";
import { SommerlesBanner } from "./SommerlesBanner";
import SommerlesBooklistPreview from "./SommerlesBooklistPreview";
import { SommerlesApiStats, SommerlesStats } from "./SommerlesStats";
import { SommerlesViewRichTextBlock } from "./SommerlesViewRichTextBlock";
import SommerlesPopularReadingLinks from "./SommerlesPopularReadingLinks";

type SommerlesProps = {
  sommerles: ResolvedSommerles;
  sommerlesApiStats?: SommerlesApiStats;
  hasSommerlesPopularReading?: boolean;
};

export const SommerlesView = ({ sommerles, sommerlesApiStats, hasSommerlesPopularReading }: SommerlesProps) => {
  const { t, lang } = useTranslation();
  const { site } = useCommonData();

  const bookLists = sommerles.bookLists?.filter(isNonNil) ?? [];
  const recommendations = sommerles.recommendations?.filter(isNonNil) ?? [];
  const hasListsOrRecommendations = !!bookLists.length || !!recommendations.length;

  const { events, showingAllEvents, toggleAllEvents, haveAdditionalEvents } = useCampaignEvents(sommerles.events);
  const character1 = sommerles.templateContent?.character1Image;
  const character2 = sommerles.templateContent?.character2Image;
  const detail1 = sommerles.templateContent?.detail1;
  const detail2 = sommerles.templateContent?.detail2;
  const detail3 = sommerles.templateContent?.detail3;
  const detail4 = sommerles.templateContent?.detail4;
  const detail6 = sommerles.templateContent?.detail6;
  const detail7 = sommerles.templateContent?.detail7;
  const detail8 = sommerles.templateContent?.detail8;

  if (!sommerles.templateContent?.backgroundImage?.asset) {
    return (
      <DefaultContainer margin={{ base: "0", lg: "auto" }}>
        {/* Banner shows information about missing content in non-production environments */}
        <SommerlesBanner sommerles={sommerles} />
      </DefaultContainer>
    );
  }

  return (
    <DefaultContainer
      margin={{ base: "0 0 3rem", sm: "0 auto 5rem", md: "0 auto 5rem" }}
      padding={{ base: 0, md: "3rem 0" }}
      position="relative"
    >
      <Edit
        doc={sommerles}
        label="Oppdater Sommerles-side"
        position="absolute"
        left="50%"
        transform="translateX(-50%)"
        top="-2rem"
      />
      <VStack gap={{ base: "3rem", sm: "4rem" }} style={{ hyphens: "none" }}>
        <SommerlesBanner sommerles={sommerles} />
        <CampaignViewSection fontSize="lg" maxWidth="32rem" alignItems="center" gap="1.5rem" className="group">
          <Text display="flex" flexDirection="column">
            <Text as="span" marginBottom="1rem">
              {t("Går du i 1.–7. klasse?")} {t("Bli med på sommerens beste lesekampanje!")}
            </Text>
            <Text as="span">{t("Les så mye du klarer, og hent fine premier på biblioteket.")}</Text>
          </Text>
          <ButtonBS
            as="a"
            href={`${sommerles.templateContent.url}/?lang=${lang}` /* TODO: Is there a more direct link to enroll? */}
            variant="secondary"
            zIndex="1" // Make sure <Character>s don't overlap the button and make it unclickable
          >
            {isCampaignSeason(sommerles) ? t("Meld deg på") : `${t("Les mer om")} ${t("Sommerles")}`}
            <ExternalLink />
          </ButtonBS>
        </CampaignViewSection>
        {!!events.length && (
          <CampaignViewSection headline={t("Kom på våre Sommerles-arrangementer!")} zIndex={1}>
            <EventsList events={events} />
            {haveAdditionalEvents && (
              <Center width="100%" zIndex="1">
                <ButtonBS onClick={toggleAllEvents} variant="secondary">
                  {showingAllEvents ? t("Vis færre") : t("Vis alle")}
                </ButtonBS>
              </Center>
            )}
          </CampaignViewSection>
        )}
        {site?.municipality?.municipalityNumber && (
          <SommerlesStats sommerles={sommerles} sommerlesApiStats={sommerlesApiStats} />
        )}
        {detail2 && (
          <PynteLinjeWrapper>
            <Box display="flex" alignItems="center" justifyContent="flex-start">
              <SanityImagePreservingAspectRatio
                image={detail3}
                resolution={1000}
                position="absolute"
                width="35%"
                transform="rotate(0.5turn)"
                boxShadow="none"
                zIndex={0}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center">
              <SanityImagePreservingAspectRatio
                image={detail8}
                resolution={1000}
                position="absolute"
                width="15%"
                transform="translate(50%, 25%) scale(-1, 1)"
                boxShadow="none"
                zIndex={0}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <SanityImagePreservingAspectRatio
                image={detail2}
                resolution={1000}
                position="absolute"
                width="30%"
                transform="scale(-1, 1)"
                boxShadow="none"
                zIndex={0}
              />
            </Box>
          </PynteLinjeWrapper>
        )}
        {hasListsOrRecommendations && (
          <>
            {recommendations.length > 0 && (
              <CampaignViewSection headline={t("Finn noe å lese")}>
                <ListsAndRecommendationsList
                  listsAndRecommendations={sortRecommendationBookListByDate(recommendations) ?? []}
                  marginBottom="3rem"
                />
              </CampaignViewSection>
            )}
            {bookLists.length > 0 && (
              <CampaignViewSection>
                {bookLists.map((bookList) => (
                  <SommerlesBooklistPreview key={bookList._id} list={bookList} />
                ))}
              </CampaignViewSection>
            )}
            {detail1 && (
              <PynteLinjeWrapper>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <SanityImagePreservingAspectRatio
                    image={detail1}
                    resolution={1000}
                    position="absolute"
                    width="50%"
                    transform="translate(-35%, 5%) rotate(0.5turn) scale(1, -1)"
                    boxShadow="none"
                    zIndex={0}
                  />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <SanityImagePreservingAspectRatio
                    image={detail7}
                    resolution={1000}
                    position="absolute"
                    width="12%"
                    transform="translate(-10%, 25%) scale(-1, 1)"
                    boxShadow="none"
                    zIndex={0}
                  />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <SanityImagePreservingAspectRatio
                    image={detail6}
                    resolution={1000}
                    position="absolute"
                    width="10%"
                    transform="translate(-200%, 0) scale(-1, 1)"
                    boxShadow="none"
                    zIndex={0}
                  />
                </Box>
              </PynteLinjeWrapper>
            )}
          </>
        )}
        {hasSommerlesPopularReading && (
          <CampaignViewSection headline={t("Se hva andre deltakere leser!")}>
            <SommerlesPopularReadingLinks />
          </CampaignViewSection>
        )}
        <SommerlesViewRichTextBlock sommerles={sommerles} />
        {detail1 && (
          <PynteLinjeWrapper>
            <Box display="flex" alignItems="center" justifyContent="flex-start">
              <SanityImagePreservingAspectRatio
                image={detail4}
                resolution={1000}
                position="absolute"
                width="80%"
                transform="translate(-35%, 5%) rotate(0.5turn) scale(1, -1)"
                boxShadow="none"
                zIndex={0}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end" pointerEvents="none">
              <SanityImagePreservingAspectRatio
                image={character1}
                resolution={1000}
                position="absolute"
                width={{ base: "25%", md: "20%" }}
                transform={{ base: "translate(-50%, 45%) scale(-1, 1)", md: "translate(-75%, 100%) scale(-1, 1)" }}
                boxShadow="none"
                zIndex={1}
              />
              <SanityImagePreservingAspectRatio
                image={character2}
                resolution={1000}
                position="absolute"
                width={{ base: "38%", md: "30%" }}
                transform={{ base: "translate(-15%, 20%) scale(-1, 1)", md: "translate(-30%, 60%) scale(-1, 1)" }}
                boxShadow="none"
                zIndex={0}
              />
            </Box>
          </PynteLinjeWrapper>
        )}
      </VStack>
    </DefaultContainer>
  );
};

const PynteLinjeWrapper = (props: { children: ReactNode }) => (
  <Flex height="6rem" width="100vw" alignItems="center" justifyContent="space-between" aria-hidden {...props} />
);
